import {
  DEFAULT_BRANDING,
  DEFAULT_LOGIN_PAGE,
  DEFAULT_THEME,
  type NavPage,
  type PublicSite,
  type SiteCustomisation,
} from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { isPreviewMode } from '@/lib/customisation/preview';
import { publicSiteQuery } from '@/lib/customisation/queries';
import { useTheme } from '@/lib/theme';
import { applySite } from './apply';

/**
 * The site a visitor sees. `useSite()` never returns nothing: Hearth's defaults stand in
 * while the published site loads (or when it can't be loaded), so the shell renders
 * identically to an uncustomised panel rather than empty.
 */
export const DEFAULT_PUBLIC_SITE: PublicSite = {
  tenant: { slug: 'default', name: 'Hearth' },
  branding: DEFAULT_BRANDING,
  logos: { primary: null, light: null, dark: null, compact: null, favicon: null, login: null },
  theme: DEFAULT_THEME,
  login: { ...DEFAULT_LOGIN_PAGE, backgroundImageUrl: null },
  customCss: '',
  pages: [],
  preview: false,
  customised: false,
};

interface SiteContextValue {
  site: PublicSite;
  /** False until the published site has arrived (defaults are showing). */
  loaded: boolean;
}

const SiteContext = createContext<SiteContextValue>({ site: DEFAULT_PUBLIC_SITE, loaded: false });

/** Where an uploaded asset is served from. */
export const assetUrl = (id: string) => `/api/customisation/assets/${id}`;

/**
 * A site draft as the customer-facing shape, for the studio's live previews: asset ids
 * become URLs and the nav pages are whatever the caller knows about.
 */
export function publicSiteFromDraft(
  draft: SiteCustomisation,
  opts: { tenant?: PublicSite['tenant']; pages?: NavPage[] } = {},
): PublicSite {
  const ref = (id: string | null) => (id ? assetUrl(id) : null);
  const l = draft.branding.logos;
  return {
    tenant: opts.tenant ?? DEFAULT_PUBLIC_SITE.tenant,
    branding: draft.branding,
    logos: {
      primary: ref(l.primary),
      light: ref(l.light),
      dark: ref(l.dark),
      compact: ref(l.compact),
      favicon: ref(l.favicon),
      login: ref(l.login),
    },
    theme: draft.theme,
    login: { ...draft.login, backgroundImageUrl: ref(draft.login.backgroundImage) },
    customCss: draft.customCss,
    pages: opts.pages ?? [],
    preview: true,
    customised: true,
  };
}

/**
 * Loads the published site (the draft in preview mode) and applies it to the document:
 * theme variables, appearance attributes, custom CSS, favicon, title and the default theme.
 */
export function SiteProvider({ children }: { children: ReactNode }) {
  // The key is computed here rather than taken from publicSiteQuery, so preview mode adopted
  // from the URL after that module loaded is still honoured.
  const query = useQuery({
    ...publicSiteQuery,
    queryKey: ['customisation', 'site', isPreviewMode()],
  });
  const { theme, setDefault } = useTheme();
  const site = query.data ?? DEFAULT_PUBLIC_SITE;
  const loaded = query.data !== undefined;

  useEffect(() => {
    if (!loaded) return;
    applySite(site, theme);
  }, [site, loaded, theme]);

  useEffect(() => {
    if (!loaded) return;
    setDefault(site.theme.appearance.defaultTheme, site.theme.appearance.allowThemeSwitch);
  }, [
    loaded,
    site.theme.appearance.defaultTheme,
    site.theme.appearance.allowThemeSwitch,
    setDefault,
  ]);

  const value = useMemo(() => ({ site, loaded }), [site, loaded]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

/** The visitor's site: branding, logos, theme, login page and nav pages. Never empty. */
export function useSite(): PublicSite {
  return useContext(SiteContext).site;
}

/** The site plus whether it is the real one yet (for the odd case that must wait). */
export function useSiteState(): SiteContextValue {
  return useContext(SiteContext);
}
